import { request } from "$/lib/api"
import {
  CreateInvoice,
  Invoice,
  InvoiceListItem,
  InvoiceReferences,
  Route,
  SetInvoiceInvoiced,
  UpdateInvoiceJobs,
  UpdateInvoiceRows,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

async function fetchListByCompany(
  companyId: string,
  companyContactPersonId: string | null,
): Promise<InvoiceListItem[]> {
  const params: Record<string, string> = {}
  if (companyContactPersonId)
    params.companyContactPersonId = companyContactPersonId
  return await request<InvoiceListItem[]>({
    url: populateRoute(Route.INVOICE__FIND_MANY_BY_COMPANY, companyId),
    method: "GET",
    params,
  })
}

async function fetchByCompanyAndNumber(
  companyId: string,
  displayNumber: number,
): Promise<Invoice> {
  return await request<Invoice>({
    url: populateRoute(
      Route.INVOICE__FIND_ONE_BY_NUMBER,
      companyId,
      String(displayNumber),
    ),
    method: "GET",
  })
}

async function fetchById(invoiceId: string): Promise<Invoice> {
  return await request<Invoice>({
    url: populateRoute(Route.INVOICE__FIND_ONE, invoiceId),
    method: "GET",
  })
}

async function createInvoice(
  storeId: string,
  data: CreateInvoice,
): Promise<Invoice> {
  return await request<Invoice>({
    url: populateRoute(Route.INVOICE__CREATE, storeId),
    method: "POST",
    data,
  })
}

async function createInvoiceFromJob(jobId: string): Promise<Invoice> {
  return await request<Invoice>({
    url: populateRoute(Route.INVOICE__CREATE_FROM_JOB, jobId),
    method: "POST",
  })
}

async function updateRows(
  invoiceId: string,
  data: UpdateInvoiceRows,
): Promise<Invoice> {
  return await request<Invoice>({
    url: populateRoute(Route.INVOICE__UPDATE_ROWS, invoiceId),
    method: "PATCH",
    data,
  })
}

async function updateJobs(
  invoiceId: string,
  data: UpdateInvoiceJobs,
): Promise<Invoice> {
  return await request<Invoice>({
    url: populateRoute(Route.INVOICE__UPDATE_JOBS, invoiceId),
    method: "PATCH",
    data,
  })
}

async function updateReferences(
  invoiceId: string,
  data: InvoiceReferences,
): Promise<Invoice> {
  return await request<Invoice>({
    url: populateRoute(Route.INVOICE__UPDATE_REFERENCES, invoiceId),
    method: "PATCH",
    data,
  })
}

async function deleteInvoice(invoiceId: string): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.INVOICE__DELETE, invoiceId),
    method: "DELETE",
  })
}

async function setInvoiced(
  invoiceId: string,
  data: SetInvoiceInvoiced,
): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.INVOICE__SET_INVOICED, invoiceId),
    method: "PATCH",
    data,
  })
}

async function sendToSpecter(invoiceId: string): Promise<Invoice> {
  return await request<Invoice>({
    url: populateRoute(Route.INVOICE__SEND_TO_SPECTER, invoiceId),
    method: "POST",
  })
}

const invoiceQueries = {
  all: () => ["invoices"],
  listByCompany: (companyId: string, companyContactPersonId: string | null) =>
    queryOptions({
      queryKey: [
        ...invoiceQueries.all(),
        "list",
        companyId,
        companyContactPersonId,
      ],
      queryFn: () => fetchListByCompany(companyId, companyContactPersonId),
    }),
  detail: (invoiceId: string) =>
    queryOptions({
      queryKey: [...invoiceQueries.all(), "detail", invoiceId],
      queryFn: () => fetchById(invoiceId),
    }),
  byCompanyAndNumber: (companyId: string, displayNumber: number) =>
    queryOptions({
      queryKey: [
        ...invoiceQueries.all(),
        "by-company-number",
        companyId,
        displayNumber,
      ],
      queryFn: () => fetchByCompanyAndNumber(companyId, displayNumber),
    }),
}

export function useInvoiceMutations(storeId: string) {
  const queryClient = useQueryClient()
  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: invoiceQueries.all() }),
      queryClient.invalidateQueries({ queryKey: ["jobs"] }),
      queryClient.invalidateQueries({ queryKey: ["calendar"] }),
    ])
  }
  // Putting jobs on an invoice can change their Referens and Etjänstnummer.
  const invalidateWithJobs = async () => {
    await Promise.all([
      invalidate(),
      queryClient.invalidateQueries({ queryKey: ["job"] }),
    ])
  }

  return {
    createMutation: useMutation({
      mutationFn: (data: CreateInvoice) => createInvoice(storeId, data),
      onSettled: invalidateWithJobs,
    }),
    createFromJobMutation: useMutation({
      mutationFn: (jobId: string) => createInvoiceFromJob(jobId),
      onSettled: invalidate,
    }),
    updateRowsMutation: useMutation({
      mutationFn: ({
        invoiceId,
        data,
      }: { invoiceId: string; data: UpdateInvoiceRows }) =>
        updateRows(invoiceId, data),
      onSettled: invalidate,
    }),
    updateJobsMutation: useMutation({
      mutationFn: ({
        invoiceId,
        data,
      }: { invoiceId: string; data: UpdateInvoiceJobs }) =>
        updateJobs(invoiceId, data),
      onSettled: invalidateWithJobs,
    }),
    updateReferencesMutation: useMutation({
      mutationFn: ({
        invoiceId,
        data,
      }: { invoiceId: string; data: InvoiceReferences }) =>
        updateReferences(invoiceId, data),
      onSettled: invalidateWithJobs,
    }),
    deleteMutation: useMutation({
      mutationFn: (invoiceId: string) => deleteInvoice(invoiceId),
      onSettled: invalidate,
    }),
    setInvoicedMutation: useMutation({
      mutationFn: ({
        invoiceId,
        invoiced,
      }: { invoiceId: string; invoiced: boolean }) =>
        setInvoiced(invoiceId, { invoiced }),
      onSettled: invalidate,
    }),
    sendToSpecterMutation: useMutation({
      mutationFn: (invoiceId: string) => sendToSpecter(invoiceId),
      onSettled: async () => {
        // The jobs are now locked, so their detail pages must refetch too.
        await Promise.all([
          invalidate(),
          queryClient.invalidateQueries({ queryKey: ["job"] }),
        ])
      },
    }),
  }
}

export default invoiceQueries
